import { ChevronDownIcon } from '@rsos/assets/icons';
import type { IarAgency } from '@rsos/reduxDataStore/src/features/eventShare/types';
import AgencyCheckbox from './AgencyCheckbox';
import AgencyMemberList from './AgencyMemberList';
import styles from './IarAgencySelector.module.scss';
import type { IarSelectedMember } from './helpers';
import { useAgencyHasMatches } from './iarAgencySelectorHelpers';

interface AgencyAccordionProps {
  org: string;
  agency: IarAgency;
  searchTerm: string;
  selectedMembers: IarSelectedMember[];
  onChange: (members: IarSelectedMember[]) => void;
  isExpanded: boolean;
  onToggleExpand: (agencyId: number) => void;
  getAgencyMemberCount: (agencyId: number) => number;
  onAgencyToggle: (
    agency: IarAgency,
    visibleSelections: IarSelectedMember[],
  ) => void;
}

/** A single collapsible agency row: select-all checkbox + caret + member list. */
const AgencyAccordion = ({
  org,
  agency,
  searchTerm,
  selectedMembers,
  onChange,
  isExpanded,
  onToggleExpand,
  getAgencyMemberCount,
  onAgencyToggle,
}: AgencyAccordionProps) => {
  const hasMatches = useAgencyHasMatches(org, agency.legacyId, searchTerm);

  if (!hasMatches) return null;

  // While a search is active, force-expand matching agencies so their results
  // are visible without the user having to expand each agency manually.
  const expanded = isExpanded || searchTerm.trim().length > 0;

  const agencyMemberCount = getAgencyMemberCount(agency.legacyId);
  const headerClassName = `${styles.agencyHeader} ${
    expanded ? styles.agencyHeaderExpanded : ''
  }`;
  const caretClassName = `${styles.agencyCaret} ${
    expanded ? styles.agencyCaretExpanded : ''
  }`;

  return (
    <div className={styles.agencyAccordionWrapper}>
      <div className={headerClassName}>
        <div
          className={styles.checkboxWrapper}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <AgencyCheckbox
            org={org}
            agency={agency}
            selectedCount={agencyMemberCount}
            onToggle={onAgencyToggle}
            searchTerm={searchTerm}
          />
        </div>
        <button
          type="button"
          className={caretClassName}
          onClick={() => onToggleExpand(agency.legacyId)}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          <ChevronDownIcon color="secondaryText" width={10} height={10} />
        </button>
      </div>

      {expanded && (
        <div className={styles.agencyContent}>
          <AgencyMemberList
            org={org}
            agency={agency}
            selectedMembers={selectedMembers}
            onChange={onChange}
            searchTerm={searchTerm}
          />
        </div>
      )}
    </div>
  );
};

export default AgencyAccordion;
